import { PayloadAction } from '@reduxjs/toolkit';
import { isNumber } from 'lodash-es';

import { ICartEntry } from '@rbi-ctg/menu';
import { createAppSlice } from 'state/global-state/utils';
import LocalStorage, { StorageKeys } from 'utils/local-storage';

import { ICartState } from './cart.types';

const preloadedOrder = LocalStorage.getItem(StorageKeys.ORDER);

export const initialState: ICartState = {
  cartEntries: (preloadedOrder?.cartEntries || []) as ICartEntry[],
};

export const cartSlice = createAppSlice({
  name: 'cart',
  initialState,
  reducers: {
    addCartEntry: (state, { payload }: PayloadAction<ICartEntry>) => {
      state.cartEntries.push(payload);
    },
    // FIXME: replace this action because is unsafe to delegate the responsibility of mutating the state to the consumers.
    // Currently used for swaps.
    overrideCartEntries: (state, { payload }: PayloadAction<ICartEntry[]>) => {
      state.cartEntries = payload;
    },
    // used to remove single and multiple entries
    removeCartEntries: (state, { payload: idsToRemove }: PayloadAction<Set<string>>) => {
      state.cartEntries = state.cartEntries.filter(entry => !idsToRemove.has(entry.cartId));

      // Reset cart if only `invalid` entries remains
      // TODO: check for `isDiscountOffer(state.loyalty.appliedOffers[cartId])`
      if (state.cartEntries.every(entry => entry.isExtra || entry.isDonation)) {
        state.cartEntries = [];
      }
    },
    // FIXME: replace this action because is unsafe to delegate the responsibility of mutating the state to the consumers.
    repriceCartEntries: (state, { payload }: PayloadAction<ICartEntry[]>) => {
      // Safety measure in case we are overriding the cartEntries with stale data
      if (payload?.length !== state.cartEntries.length) {
        return;
      }
      state.cartEntries = payload;
    },
    resetCart: state => {
      state.cartEntries = [];
    },
    updateCartEntry: (
      state,
      { payload }: PayloadAction<{ cartEntry: ICartEntry; cartId: string }>
    ) => {
      const { cartEntry, cartId } = payload;
      const index = state.cartEntries.findIndex(entry => entry.cartId === cartId);
      state.cartEntries =
        index !== -1
          ? [...state.cartEntries.slice(0, index), cartEntry, ...state.cartEntries.slice(index + 1)]
          : [...state.cartEntries, cartEntry];
    },
    updateQuantityAndPrice: (
      state,
      { payload }: PayloadAction<{ cartId: string; quantity: number; price?: number }>
    ) => {
      const { cartId, quantity, price } = payload;
      state.cartEntries = state.cartEntries.map(entry => {
        if (entry.cartId === cartId) {
          entry.quantity = quantity;
          if (isNumber(price)) {
            entry.price = price;
          }
        }
        return entry;
      });
    },
  },
});
